/**
 * Dashboard insight → AI Assistant entry points (contextual open + prompts).
 */

export const INSIGHT_CONTEXT = {
  TODAY_SALES: 'today_sales',
  TODAY_PROFIT: 'today_profit',
  SALES_OVERVIEW: 'sales_overview',
  INVENTORY_HEALTH: 'inventory_health',
  BEST_SELLERS: 'best_sellers',
};

/** Overview card ids that show the AI Assistant button */
export const OVERVIEW_CARD_AI_CONTEXT = {
  bills: INSIGHT_CONTEXT.TODAY_SALES,
  sales: INSIGHT_CONTEXT.TODAY_SALES,
  profit: INSIGHT_CONTEXT.TODAY_PROFIT,
};

const fmt = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '0';
  return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const periodLabel = (snapshot) => snapshot?.timeRange?.label || 'this period';

export const getInsightInitialPrompt = (contextId, snapshot) => {
  const period = periodLabel(snapshot);
  const o = snapshot?.overview || {};
  const sales = snapshot?.salesOverview || {};
  const metrics = sales.metrics || {};
  const inv = snapshot?.inventory || {};
  const best = snapshot?.bestSellers || [];

  switch (contextId) {
    case INSIGHT_CONTEXT.TODAY_SALES:
      return (
        `You are analyzing the "${period}" sales overview on my dashboard. ` +
        `For this period: ${fmt(o.totalBills)} bills/orders, total sales amount ₹${fmt(o.totalSalesAmount)}. ` +
        `Sales overview chart period is "${sales.period || 'monthly'}" with this month ₹${fmt(metrics.thisMonth)} and last month ₹${fmt(metrics.lastMonth)}. ` +
        `Give a concise executive summary: how orders and revenue look for ${period}, how it compares to the monthly chart, and the top 2–3 actions I should take. Use only the dashboard snapshot JSON.`
      );

    case INSIGHT_CONTEXT.TODAY_PROFIT:
      return (
        `Analyze profit for "${period}" on my dashboard. ` +
        `Sales ₹${fmt(o.totalSalesAmount)}, expenses ₹${fmt(o.totalExpenses)}, profit ₹${fmt(o.totalProfit)}. ` +
        `Is margin healthy? What is driving profit up or down, and what should I focus on this week? Use only snapshot data.`
      );

    case INSIGHT_CONTEXT.SALES_OVERVIEW: {
      const chartPeriod = sales.period === 'weekly' ? 'weekly' : 'monthly';
      return (
        `Analyze my Sales Overview (${chartPeriod} chart). ` +
        `This month: ₹${fmt(metrics.thisMonth)}, last month: ₹${fmt(metrics.lastMonth)}. ` +
        `Chart has ${(sales.chartLabels || []).length} points. ` +
        `Explain the trend, spikes or drops, and whether performance is improving. Recommend next steps using only the snapshot.`
      );
    }

    case INSIGHT_CONTEXT.INVENTORY_HEALTH:
      return (
        `Summarize inventory health from my dashboard snapshot: ` +
        `${fmt(inv.totalProducts)} total products, ${fmt(inv.inStock)} in stock, ${fmt(inv.lowStock)} low stock, ${fmt(inv.outOfStock)} out of stock. ` +
        `Highlight operational risks, restock priorities, and business impact. Use low-stock item details if present in the snapshot.`
      );

    case INSIGHT_CONTEXT.BEST_SELLERS: {
      const topNames = best.slice(0, 3).map((p) => p.productName).filter(Boolean).join(', ');
      return (
        `Analyze best selling products on my dashboard${topNames ? ` (top: ${topNames})` : ''}. ` +
        `Is revenue concentrated on few SKUs? Which products drive growth, and what diversification or promotion actions do you recommend? Use only snapshot data.`
      );
    }

    default:
      return `Give an executive summary of my dashboard for ${period}. Use only the snapshot data.`;
  }
};

const INSIGHT_SUGGESTION_POOLS = {
  [INSIGHT_CONTEXT.TODAY_SALES]: [
    {
      label: 'Orders vs revenue alignment',
      prompt:
        'For this period, are bill count and sales amount aligned? Explain any gap and what to verify in operations.',
    },
    {
      label: 'Compare to last month',
      prompt:
        'Compare this period’s sales to the sales overview monthly figures in the snapshot. Is today/this week on track?',
    },
    {
      label: 'Improve sales this period',
      prompt:
        'Based on current sales metrics in the snapshot, what 3 practical actions can increase orders and revenue this period?',
    },
    {
      label: 'Spot unusual patterns',
      prompt:
        'From the sales chart and overview totals, flag anything unusual or worth investigating.',
    },
  ],
  [INSIGHT_CONTEXT.TODAY_PROFIT]: [
    {
      label: 'Top levers to improve margin',
      prompt: 'What are the top 3 levers to improve profit margin using snapshot sales and expenses?',
    },
    {
      label: 'Are expenses too high?',
      prompt: 'Are expenses proportionate to sales in the snapshot? What should we audit first?',
    },
    {
      label: 'Is profit sustainable?',
      prompt: 'Is the current profit level sustainable? What risks could erode it next month?',
    },
    {
      label: 'Break-even sensitivity',
      prompt: 'How sensitive is profit to a 10% drop in sales or rise in expenses based on snapshot figures?',
    },
  ],
  [INSIGHT_CONTEXT.SALES_OVERVIEW]: [
    {
      label: 'Explain the chart trend',
      prompt: 'Walk through the sales chart trend point by point and explain what changed recently.',
    },
    {
      label: 'This month vs last month',
      prompt: 'Deep dive: why is this month higher or lower than last month per the snapshot KPIs?',
    },
    {
      label: 'Forecast next month',
      prompt: 'Based on the chart pattern, give a cautious outlook for next month and what to watch.',
    },
    {
      label: 'Validate a sales spike',
      prompt: 'If you see a spike in the chart, help validate whether it is real and what likely caused it.',
    },
  ],
  [INSIGHT_CONTEXT.INVENTORY_HEALTH]: [
    {
      label: 'Restock priority list',
      prompt: 'From snapshot inventory and low-stock items, give a prioritized restock list with reasoning.',
    },
    {
      label: 'Out-of-stock impact',
      prompt: 'Estimate business impact of out-of-stock SKUs using the snapshot.',
    },
    {
      label: 'Reduce low-stock risk',
      prompt: 'How can we reduce low-stock incidents operationally this month?',
    },
    {
      label: 'Healthy vs at-risk SKUs',
      prompt: 'Separate inventory into healthy vs at-risk buckets using snapshot counts.',
    },
  ],
  [INSIGHT_CONTEXT.BEST_SELLERS]: [
    {
      label: 'Concentration risk',
      prompt: 'How concentrated is revenue on top products? What happens if #1 underperforms?',
    },
    {
      label: 'Promote underperformers',
      prompt: 'Which products in the snapshot could be promoted to diversify revenue?',
    },
    {
      label: 'Stock top sellers',
      prompt: 'Do we have enough stock for top sellers given inventory snapshot? Any mismatch?',
    },
    {
      label: 'Pricing / mix ideas',
      prompt: 'Suggest pricing or product-mix ideas based on best seller performance in the snapshot.',
    },
  ],
};

export const getInsightSuggestions = (contextId, snapshot, max = 4) => {
  const pool = INSIGHT_SUGGESTION_POOLS[contextId];
  if (!pool) return [];
  return pool.slice(0, max).map((s) => ({
    ...s,
    prompt: `${s.prompt} Use only the current dashboard snapshot.`,
  }));
};

export default INSIGHT_CONTEXT;
